import React from "react";
import PropTypes from "prop-types";
import { format } from "date-fns";
import ReactCountryFlag from "react-country-flag";
import get from "lodash-es/get";

// Material
import { makeStyles } from "@material-ui/core/styles";
import Avatar from "@material-ui/core/Avatar";
import Paper from "@material-ui/core/Paper";

// Hooks
import useTranslate from "src/hooks/useTranslate";

// Icon
import phonecall from "../../assets/icons/phonecall.svg";

// Config
import { userTypes } from "../../config/values";
import colors from "../../config/colors";
import styles from "../../config/styles";

// Components
import ContentCardHeader from "../ContentCardHeader";
import Text from "../Text";
import CaptureVictimButton from "../CaptureVictimButton/CaptureVictimButton";

const useStyles = makeStyles(theme => ({
  root: {
    border: styles.values.border
  },
  title: {
    fontSize: 24
  },
  contentContainer: {
    display: "flex",
    flexDirection: "row",
    padding: theme.spacing(3, 4, 4),
    alignItems: "flex-start"
  },
  avatarWrapper: {
    position: "relative",
    marginRight: theme.spacing(3),
    "&:hover": {
      "& button": {
        opacity: "initial"
      }
    }
  },
  editButton: {
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: "translateX(-50%) translateY(-50%)",
    zIndex: "99",
    opacity: 0,
    filter: "brightness(10)"
  },
  addButton: {
    height: 86,
    width: 86,
    border: "1px, solid, #E0E0E0",
    background: "#F7F7FA"
  },
  avatar: {
    height: 86,
    width: 86
  },
  contentWrapper: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    maxWidth: "335px"
  },
  heading: {
    marginBottom: "4px",
    fontSize: 13,
    color: colors.riverBed
  },
  victimName: {
    marginBottom: "12px",
    fontSize: 22,
    color: colors.riverBed
  },
  valuesWrapper: {
    marginBottom: "12px"
  },
  value: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center"
  },
  contentLabel: {
    color: "#454F63"
  },
  placeOfExamination: {
    maxWidth: 160
  },
  boxPhoneNumber: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center"
  },
  phoneNumber: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center"
  },
  flag: {
    fontSize: "18px !important",
    marginRight: "6px"
  },
  areaCode: {
    marginRight: "6px",
    fontSize: "14px"
  },
  number: {
    marginRight: "6px",
    fontSize: "14px"
  },
  numberWitness: {
    fontSize: "14px"
  },
  callIcon: {
    fontSize: 12
  }
}));

const VictimInfoCard = ({ data }) => {
  const classes = useStyles();
  const getText = useTranslate();

  const dateOfBirth = get(data, "patient.dateOfBirth", false);
  const birthDate = dateOfBirth && format(new Date(dateOfBirth), "dd/MM/yy");
  const isWitness = data.userCapacity === userTypes.Witness;
  const photo = get(data, "patient.profilePhotoURL", null);

  return (
    <Paper elevation={0} className={classes.root}>
      <ContentCardHeader>
        <Text
          className={classes.title}
          text="caseDetailPage.victimInfoCard.title"
          type="heading2"
        />
      </ContentCardHeader>
      <div className={classes.contentContainer}>
        <div className={classes.avatarWrapper}>
          {data.isDraft && (
            <CaptureVictimButton
              className={photo ? classes.editButton : classes.addButton}
            />
          )}
          {(photo || !data.isDraft) && (
            <Avatar src={photo} variant="circle" className={classes.avatar} />
          )}
        </div>
        <div className={classes.contentWrapper}>
          <Text
            className={classes.heading}
            text="caseDetailPage.victimInfoCard.name"
            type="body"
          />
          <Text type="heading6" className={classes.victimName}>
            {data.patientName.trim() ||
              getText("common.valueLabels.notProvided")}
          </Text>
          <div className={classes.valuesWrapper}>
            <div className={classes.value}>
              <Text
                text="common.valueLabels.birthDate"
                type="contentLabel3"
                className={classes.contentLabel}
              />
              <Text align="right" type="contentLabel2">
                {birthDate || getText("common.valueLabels.notProvided")}
              </Text>
            </div>
          </div>
          <div className={classes.boxPhoneNumber}>
            <Text
              className={classes.contentLabel}
              text="createCasePage.formContent.victimPhoneNumberLabel"
              type="contentLabel3"
            />
            <div className={classes.phoneNumber}>
              {isWitness ? (
                <Text className={classes.numberWitness} type="body">
                  ********
                </Text>
              ) : (
                <>
                  <ReactCountryFlag
                    className={classes.flag}
                    countryCode={get(
                      data,
                      "patient.phoneNumber.isoCountryCode",
                      ""
                    )}
                  />
                  <Text className={classes.areaCode} type="body">
                    {get(data, "patient.phoneNumber.callingCountryCode", "")}
                  </Text>
                  <Text className={classes.number} type="body">
                    {get(data, "patient.phoneNumber.nationalNumber", "")}
                  </Text>
                  <img
                    src={phonecall}
                    alt="Call"
                    className={classes.callIcon}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Paper>
  );
};

VictimInfoCard.propTypes = {
  data: PropTypes.shape({
    dateTimeOfExamination: PropTypes.string,
    patient: PropTypes.shape({
      dateOfBirth: PropTypes.string,
      phoneNumber: PropTypes.shape({
        countryCode: PropTypes.string,
        number: PropTypes.string
      }),
      profilePhotoURL: PropTypes.string
    }),
    patientName: PropTypes.string,
    placeOfExamination: PropTypes.shape({
      name: PropTypes.string
    }),
    userCapacity: PropTypes.string,
    isDraft: PropTypes.bool
  }).isRequired
};

export default VictimInfoCard;
